import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Bill, BillFilters, BillFormData, BillPaymentFormData } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
export const billKeys = {
  all: ["bills"] as const,
  lists: () => [...billKeys.all, "list"] as const,
  list: (filters?: BillFilters) => [...billKeys.lists(), { filters }] as const,
  details: () => [...billKeys.all, "detail"] as const,
  detail: (id: number) => [...billKeys.details(), id] as const,
  upcoming: (days?: number) => [...billKeys.all, "upcoming", { days }] as const,
  overdue: () => [...billKeys.all, "overdue"] as const,
  summary: () => [...billKeys.all, "summary"] as const,
  payments: () => [...billKeys.all, "payments"] as const,
  paymentHistory: (billId: number) => [...billKeys.payments(), "history", billId] as const,
  paymentStats: (billId: number) => [...billKeys.payments(), "stats", billId] as const,
};

// Hooks

/**
 * Get all bills with optional filters
 */
export function useBills(filters?: BillFilters) {
  return useQuery({
    queryKey: billKeys.list(filters),
    queryFn: () => apiClient.getBills(filters),
  });
}

/**
 * Get a single bill by ID
 */
export function useBill(id: number) {
  return useQuery({
    queryKey: billKeys.detail(id),
    queryFn: () => apiClient.getBill(id),
    enabled: !!id,
  });
}

/**
 * Get bill summary for dashboard
 */
export function useBillSummary() {
  return useQuery({
    queryKey: billKeys.summary(),
    queryFn: () => apiClient.getBillSummary(),
  });
}

/**
 * Create a new bill
 */
export function useCreateBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BillFormData) => apiClient.createBill(data),
    onSuccess: async () => {
      // Invalidate all bill queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: billKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: billKeys.upcoming() }),
        queryClient.invalidateQueries({ queryKey: billKeys.overdue() }),
        queryClient.invalidateQueries({ queryKey: billKeys.summary() }),
      ]);

      toast({
        title: "Success",
        description: "Bill created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create bill",
        variant: "destructive",
      });
    },
  });
}

/**
 * Update an existing bill
 */
export function useUpdateBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BillFormData> }) =>
      apiClient.updateBill(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: billKeys.detail(id) });

      // Snapshot previous value
      const previousBill = queryClient.getQueryData<Bill>(billKeys.detail(id));

      // Optimistically update bill detail
      if (previousBill) {
        queryClient.setQueryData<Bill>(billKeys.detail(id), {
          ...previousBill,
          ...data,
        });
      }

      return { previousBill };
    },
    onError: (error: Error, variables, context) => {
      // Rollback on error
      if (context?.previousBill) {
        queryClient.setQueryData(billKeys.detail(variables.id), context.previousBill);
      }

      toast({
        title: "Error",
        description: error.message || "Failed to update bill",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Bill updated successfully",
      });
    },
    onSettled: async (data, error, variables) => {
      // Refetch to ensure consistency
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: billKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: billKeys.detail(variables.id),
        }),
        queryClient.invalidateQueries({ queryKey: billKeys.upcoming() }),
        queryClient.invalidateQueries({ queryKey: billKeys.overdue() }),
        queryClient.invalidateQueries({ queryKey: billKeys.summary() }),
      ]);
    },
  });
}

/**
 * Delete a bill (soft delete)
 */
export function useDeleteBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => apiClient.deleteBill(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: billKeys.lists() });

      // Snapshot previous values
      const previousLists = queryClient.getQueriesData<{
        bills: Bill[];
        total: number;
      }>({ queryKey: billKeys.lists() });

      // Optimistically remove from all lists
      queryClient.setQueriesData<{ bills: Bill[]; total: number }>(
        { queryKey: billKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            bills: old.bills.filter((bill) => bill.id !== id),
            total: old.total - 1,
          };
        },
      );

      return { previousLists };
    },
    onError: (error: Error, id, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      toast({
        title: "Error",
        description: error.message || "Failed to delete bill",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Bill deleted successfully",
      });
    },
    onSettled: async () => {
      // Refetch to ensure consistency
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: billKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: billKeys.upcoming() }),
        queryClient.invalidateQueries({ queryKey: billKeys.overdue() }),
        queryClient.invalidateQueries({ queryKey: billKeys.summary() }),
      ]);
    },
  });
}

/**
 * Mark a bill as paid
 */
export function useMarkBillAsPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      paidDate,
      amount,
      accountId,
      notes,
    }: {
      id: number;
      paidDate?: string;
      amount?: number;
      accountId?: number;
      notes?: string;
    }) => apiClient.markBillAsPaid(id, { paidDate, amount, accountId, notes }),
    onSuccess: async () => {
      // Invalidate all bill queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: billKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: billKeys.upcoming() }),
        queryClient.invalidateQueries({ queryKey: billKeys.overdue() }),
        queryClient.invalidateQueries({ queryKey: billKeys.summary() }),
      ]);

      toast({
        title: "Success",
        description: "Payment recorded successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to record payment",
        variant: "destructive",
      });
    },
  });
}

/**
 * Bill Payment Hooks
 */

/**
 * Get payment history for a bill
 */
export function usePaymentHistory(billId: number) {
  return useQuery({
    queryKey: billKeys.paymentHistory(billId),
    queryFn: () => apiClient.getPaymentHistoryForBill(billId),
    enabled: !!billId,
  });
}

/**
 * Get payment statistics for a bill
 */
export function usePaymentStats(billId: number) {
  return useQuery({
    queryKey: billKeys.paymentStats(billId),
    queryFn: () => apiClient.getBillPaymentStats(billId),
    enabled: !!billId,
  });
}

/**
 * Create a bill payment
 */
/**
 * Update a bill payment
 */
export function useUpdatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BillPaymentFormData> }) =>
      apiClient.updateBillPayment(id, data),
    onSuccess: async (payment) => {
      // Invalidate payment history and stats for the bill
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: billKeys.paymentHistory(payment.billId),
        }),
        queryClient.invalidateQueries({
          queryKey: billKeys.paymentStats(payment.billId),
        }),
        // Also invalidate bill lists to update status
        queryClient.invalidateQueries({ queryKey: billKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: billKeys.upcoming() }),
        queryClient.invalidateQueries({ queryKey: billKeys.overdue() }),
        queryClient.invalidateQueries({ queryKey: billKeys.summary() }),
      ]);

      toast({
        title: "Success",
        description: "Payment updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update payment",
        variant: "destructive",
      });
    },
  });
}

/**
 * Delete a bill payment
 */
export function useDeletePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number; billId: number }) => apiClient.deleteBillPayment(id),
    onSuccess: async (_, variables) => {
      // Invalidate payment history and stats for the bill
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: billKeys.paymentHistory(variables.billId),
        }),
        queryClient.invalidateQueries({
          queryKey: billKeys.paymentStats(variables.billId),
        }),
        // Also invalidate bill lists and summary
        queryClient.invalidateQueries({ queryKey: billKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: billKeys.summary() }),
      ]);

      toast({
        title: "Success",
        description: "Payment deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete payment",
        variant: "destructive",
      });
    },
  });
}
